import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import StatusBadge from '../components/ui/StatusBadge';
import { IconPlus } from '../components/ui/Icons';
import { getProductId } from '../utils/ids';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

const emptyForm = {
  name: '',
  isDefault: false,
  enabled: true,
  wappinApiUrl: 'https://api.inaiy.com',
  apiKey: '',
  channelId: '',
  tenantId: 'inaiy',
  otpEnabled: true,
  otpTemplateName: '',
  otpTemplateLanguage: 'en_US',
  welcomeEnabled: false,
  welcomeTemplateName: '',
  welcomeTemplateLanguage: 'en_US',
};

const FIELD_LABELS = {
  enabled: 'Enable WhatsApp',
  wappinApiUrl: 'Wappin API URL',
  apiKey: 'Public API Key',
  channelId: 'WhatsApp Channel ID',
  tenantId: 'Workspace (tenant)',
  productId: 'Product',
  otpTemplateName: 'OTP Template Name',
  welcomeTemplateName: 'Welcome Template Name',
};

function getClientMissingFields(config) {
  if (!config) return ['config'];
  const missing = [];
  if (config.enabled === false) missing.push('enabled');
  if (!String(config.wappinApiUrl || '').trim()) missing.push('wappinApiUrl');
  // apiKey is never returned to the UI — treat as present unless newly created empty
  if (!String(config.channelId || '').trim()) missing.push('channelId');
  if (!String(config.tenantId || '').trim()) missing.push('tenantId');
  if (!config.productId) missing.push('productId');
  if (config.otpEnabled !== false && !String(config.otpTemplateName || '').trim()) {
    missing.push('otpTemplateName');
  }
  if (config.welcomeEnabled && !String(config.welcomeTemplateName || '').trim()) {
    missing.push('welcomeTemplateName');
  }
  return missing;
}

function WhatsAppConfig() {
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [products, setProducts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(searchParams.get('productId') || '');
  const [showModal, setShowModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testingConfig, setTestingConfig] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [editingConfig, setEditingConfig] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!loading && selectedProductId) {
      fetchConfigs();
    } else if (!loading) {
      setConfigs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedProductId]);

  const fetchProducts = async () => {
    try {
      const response = await api.post('/product/list', {});
      const productsList = response.data.data?.products || [];
      setProducts(productsList);

      const fromUrl = searchParams.get('productId');
      if (fromUrl && productsList.some((p) => getProductId(p) === fromUrl)) {
        setSelectedProductId(fromUrl);
      } else if (!selectedProductId && productsList.length > 0) {
        setSelectedProductId(getProductId(productsList[0]));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigs = async () => {
    if (!selectedProductId) return;
    try {
      const response = await api.post('/admin/whatsapp-config/list', {
        productId: selectedProductId,
      });
      setConfigs(response.data.data?.configs || []);
    } catch (error) {
      console.error('Error fetching WhatsApp configs:', error);
      toast.error('Failed to load WhatsApp configurations');
      setConfigs([]);
    }
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setSelectedProductId(productId);
    if (productId) {
      setSearchParams({ productId });
    } else {
      setSearchParams({});
    }
  };

  const openCreateModal = () => {
    setEditingConfig(null);
    setForm({
      ...emptyForm,
      isDefault: configs.length === 0,
    });
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setForm({
      name: config.name || '',
      isDefault: !!config.isDefault,
      enabled: config.enabled !== false,
      wappinApiUrl: config.wappinApiUrl || 'https://api.inaiy.com',
      apiKey: '',
      channelId: config.channelId || '',
      tenantId: config.tenantId || 'inaiy',
      otpEnabled: config.otpEnabled !== false,
      otpTemplateName: config.otpTemplateName || '',
      otpTemplateLanguage: config.otpTemplateLanguage || 'en_US',
      welcomeEnabled: !!config.welcomeEnabled,
      welcomeTemplateName: config.welcomeTemplateName || '',
      welcomeTemplateLanguage: config.welcomeTemplateLanguage || 'en_US',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingConfig) {
        const payload = {
          configId: editingConfig._id,
          name: form.name,
          isDefault: form.isDefault,
          enabled: form.enabled,
          wappinApiUrl: form.wappinApiUrl,
          channelId: form.channelId,
          tenantId: form.tenantId,
          otpEnabled: form.otpEnabled,
          otpTemplateName: form.otpTemplateName,
          otpTemplateLanguage: form.otpTemplateLanguage,
          welcomeEnabled: form.welcomeEnabled,
          welcomeTemplateName: form.welcomeTemplateName || null,
          welcomeTemplateLanguage: form.welcomeTemplateLanguage || 'en_US',
        };
        if (form.apiKey) {
          payload.apiKey = form.apiKey;
        }
        await api.post('/admin/whatsapp-config/update', payload);
        toast.success('WhatsApp configuration updated');
      } else {
        await api.post('/admin/whatsapp-config/create', {
          productId: selectedProductId,
          ...form,
          welcomeTemplateName: form.welcomeTemplateName || null,
        });
        toast.success('WhatsApp configuration created');
      }
      setShowModal(false);
      fetchConfigs();
    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
      toast.error(error.response?.data?.message || 'Failed to save WhatsApp configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (config) => {
    const confirmed = await confirm({
      title: 'Delete WhatsApp configuration',
      message: `Delete "${config.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.post('/admin/whatsapp-config/delete', { configId: config._id });
      toast.success('WhatsApp configuration deleted');
      fetchConfigs();
    } catch (error) {
      console.error('Error deleting WhatsApp config:', error);
      toast.error(error.response?.data?.message || 'Failed to delete WhatsApp configuration');
    }
  };

  const handleValidate = async (config) => {
    setTestingId(config._id);
    try {
      const response = await api.post('/admin/whatsapp-config/test', {
        configId: config._id,
      });
      const isValid = response.data.data?.valid;
      const missingFields = response.data.data?.missingFields || [];
      if (isValid) {
        toast.success(`"${config.name}" configuration is valid`);
      } else {
        const labels = missingFields.map((key) => FIELD_LABELS[key] || key).join(', ');
        toast.error(
          response.data.message
          || (labels
            ? `"${config.name}" missing: ${labels}`
            : `"${config.name}" is missing required fields`),
        );
        if (missingFields.includes('tenantId') || missingFields.length > 0) {
          openEditModal(config);
        }
      }
    } catch (error) {
      console.error('Error testing WhatsApp config:', error);
      toast.error(error.response?.data?.message || 'Failed to test configuration');
    } finally {
      setTestingId(null);
    }
  };

  const openTestSendModal = (config) => {
    setTestingConfig(config);
    setTestPhone('');
    setShowTestModal(true);
  };

  const handleTestSend = async (e) => {
    e.preventDefault();
    if (!testingConfig || !testPhone.trim()) return;

    const clientMissing = getClientMissingFields(testingConfig);
    if (clientMissing.includes('tenantId')) {
      toast.error('Set Workspace (tenant) on this config first — e.g. inaiy — then save and test again.');
      setShowTestModal(false);
      openEditModal(testingConfig);
      return;
    }

    setTestingId(testingConfig._id);
    try {
      const response = await api.post('/admin/whatsapp-config/test', {
        configId: testingConfig._id,
        testPhone: testPhone.trim(),
      });
      if (response.data.data?.valid === false) {
        toast.error(response.data.message || 'Configuration is incomplete');
        setShowTestModal(false);
        openEditModal(testingConfig);
        return;
      }
      toast.success('Test OTP sent via WhatsApp');
      setShowTestModal(false);
    } catch (error) {
      console.error('Error sending test WhatsApp:', error);
      toast.error(error.response?.data?.message || 'Failed to send test message');
    } finally {
      setTestingId(null);
    }
  };

  const handleSetDefault = async (config) => {
    if (config.isDefault) return;
    try {
      await api.post('/admin/whatsapp-config/update', {
        configId: config._id,
        isDefault: true,
      });
      toast.success(`"${config.name}" is now the default`);
      fetchConfigs();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error(error.response?.data?.message || 'Failed to set default configuration');
    }
  };

  const selectedProduct = products.find((p) => getProductId(p) === selectedProductId);

  if (loading) {
    return <LoadingScreen message="Loading WhatsApp configuration..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="WhatsApp Configuration"
        description="Signup OTP and welcome templates via the Inaiy public messaging API. This is not tenant WhatsApp/Instagram Embedded Signup — that uses the Meta app + META_CONFIG_ID on the product API."
        action={
          selectedProductId ? (
            <button type="button" onClick={openCreateModal} className="btn btn--primary">
              <IconPlus width={18} height={18} />
              Add Configuration
            </button>
          ) : null
        }
      />

      <div className="alert alert--info" style={{ marginBottom: 16 }}>
        Tenant Instagram DMs use the same Meta Embedded Signup as WhatsApp (Facebook Login for Business
        configuration). Add Instagram Messaging to that configuration in the Meta App Dashboard, or set
        <code> META_INSTAGRAM_CONFIG_ID</code> on the product API. Do not store Instagram tokens on this OTP page.
      </div>

      <div className="card card__body">
        <div className="form-group">
          <label htmlFor="productId" className="form-label">
            Product <span className="required">*</span>
          </label>
          <select
            id="productId"
            className="form-select"
            value={selectedProductId}
            onChange={handleProductChange}
            required
          >
            <option value="">Select a product</option>
            {products.map((product) => {
              const productId = getProductId(product);
              return (
                <option key={productId} value={productId}>
                  {product.name}
                </option>
              );
            })}
          </select>
          <span className="form-hint">
            OTP is sent via WhatsApp when the user provides a mobile number during signup.
          </span>
        </div>
      </div>

      {!selectedProductId ? (
        <div className="card">
          <EmptyState
            title="Select a product"
            description="Pick a product above to manage its WhatsApp configurations."
          />
        </div>
      ) : configs.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No WhatsApp configurations"
            description={`Create a WhatsApp configuration for ${selectedProduct?.name || 'this product'}.`}
            action={
              <button type="button" onClick={openCreateModal} className="btn btn--primary">
                <IconPlus width={18} height={18} />
                Add Configuration
              </button>
            }
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Workspace</th>
                  <th>Channel</th>
                  <th>OTP Template</th>
                  <th>Welcome</th>
                  <th>Status</th>
                  <th>Default</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((config) => {
                  const missing = getClientMissingFields(config);
                  return (
                  <tr key={config._id}>
                    <td data-label="Name">
                      <div>{config.name}</div>
                      {missing.length > 0 ? (
                        <div className="cell-muted" style={{ marginTop: 4, fontSize: 12 }}>
                          Incomplete: {missing.map((key) => FIELD_LABELS[key] || key).join(', ')}
                        </div>
                      ) : null}
                    </td>
                    <td data-label="Workspace" className="cell-mono">{config.tenantId || '—'}</td>
                    <td data-label="Channel" className="cell-mono">{config.channelId}</td>
                    <td data-label="OTP Template">
                      {config.otpEnabled !== false ? (
                        <span>{config.otpTemplateName} ({config.otpTemplateLanguage || 'en_US'})</span>
                      ) : (
                        <span className="cell-muted">Disabled</span>
                      )}
                    </td>
                    <td data-label="Welcome">
                      {config.welcomeEnabled ? (
                        <span>{config.welcomeTemplateName}</span>
                      ) : (
                        <span className="cell-muted">Off</span>
                      )}
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={config.enabled !== false ? (missing.length ? 'Incomplete' : 'Active') : 'Disabled'} />
                    </td>
                    <td data-label="Default">
                      {config.isDefault ? (
                        <StatusBadge status="Default" />
                      ) : (
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => handleSetDefault(config)}
                        >
                          Set default
                        </button>
                      )}
                    </td>
                    <td data-label="Actions" className="cell-actions">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => openEditModal(config)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => handleValidate(config)}
                          disabled={testingId === config._id}
                        >
                          {testingId === config._id ? 'Testing...' : 'Validate'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => openTestSendModal(config)}
                        >
                          Test send
                        </button>
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          onClick={() => handleDelete(config)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingConfig ? 'Edit WhatsApp Configuration' : 'New WhatsApp Configuration'}
          subtitle={selectedProduct?.name}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Configuration name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={form.name}
                onChange={handleFormChange}
                required
                placeholder="e.g. Primary WhatsApp"
              />
            </div>

            <div className="form-group">
              <label htmlFor="isDefault" className="form-checkbox">
                <input
                  type="checkbox"
                  id="isDefault"
                  name="isDefault"
                  checked={form.isDefault}
                  onChange={handleFormChange}
                />
                <span>Use as default for this product</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="enabled" className="form-checkbox">
                <input
                  type="checkbox"
                  id="enabled"
                  name="enabled"
                  checked={form.enabled}
                  onChange={handleFormChange}
                />
                <span>Enable WhatsApp notifications</span>
              </label>
            </div>

            <div className="form-section">
              <h3 className="form-section__title">API connection</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Auth calls <code>/api/public/v1/messages</code> with your API key.
                Headers <code>x-tenant-id</code> and <code>x-product-id</code> are filled from
                <strong> Workspace</strong> below and the product selected on this page — you do not paste headers yourself.
              </p>
              <div className="form-group">
                <label htmlFor="wappinApiUrl" className="form-label">
                  Product API URL <span className="required">*</span>
                </label>
                <input
                  type="url"
                  id="wappinApiUrl"
                  name="wappinApiUrl"
                  className="form-input"
                  value={form.wappinApiUrl}
                  onChange={handleFormChange}
                  required
                  placeholder="https://api.inaiy.com"
                />
                <span className="form-hint">Production: https://api.inaiy.com</span>
              </div>
              <div className="form-group">
                <label htmlFor="apiKey" className="form-label">
                  Public API Key {!editingConfig && <span className="required">*</span>}
                </label>
                <input
                  type="password"
                  id="apiKey"
                  name="apiKey"
                  className="form-input"
                  value={form.apiKey}
                  onChange={handleFormChange}
                  required={!editingConfig}
                  placeholder={editingConfig ? 'Leave blank to keep unchanged' : ''}
                  autoComplete="new-password"
                />
                <span className="form-hint">
                  From <strong>the same Workspace</strong> → Developer → API Keys (messages.send).
                  Key, Workspace, and Channel ID must all belong to one workspace — mixing (e.g. pennaiappz key + inaiy channel) causes “API key tenant mismatch”.
                </span>
              </div>
              <div className="form-group">
                <label htmlFor="channelId" className="form-label">
                  WhatsApp Channel ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="channelId"
                  name="channelId"
                  className="form-input"
                  value={form.channelId}
                  onChange={handleFormChange}
                  required
                  placeholder="24-character MongoDB ObjectId"
                />
                <span className="form-hint">Channel ID from Settings → Channels for that workspace.</span>
              </div>
              <div className="form-group">
                <label htmlFor="tenantId" className="form-label">
                  Workspace (x-tenant-id) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="tenantId"
                  name="tenantId"
                  className="form-input"
                  value={form.tenantId}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g. inaiy"
                />
                <span className="form-hint">
                  Subdomain of the workspace that owns the API key and channel (example: <code>inaiy</code> for inaiy.inaiy.com).
                </span>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section__title">Signup OTP Template</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Use a Meta <strong>Authentication</strong> template (COPY_CODE). The OTP is sent as the <code>code</code> parameter.
              </p>
              <div className="form-group">
                <label htmlFor="otpEnabled" className="form-checkbox">
                  <input
                    type="checkbox"
                    id="otpEnabled"
                    name="otpEnabled"
                    checked={form.otpEnabled}
                    onChange={handleFormChange}
                  />
                  <span>Send OTP via WhatsApp during signup</span>
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="otpTemplateName" className="form-label">
                  OTP Template Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="otpTemplateName"
                  name="otpTemplateName"
                  className="form-input"
                  value={form.otpTemplateName}
                  onChange={handleFormChange}
                  required={form.otpEnabled}
                  placeholder="e.g. signup_otp"
                />
              </div>
              <div className="form-group">
                <label htmlFor="otpTemplateLanguage" className="form-label">OTP Template Language</label>
                <input
                  type="text"
                  id="otpTemplateLanguage"
                  name="otpTemplateLanguage"
                  className="form-input"
                  value={form.otpTemplateLanguage}
                  onChange={handleFormChange}
                  placeholder="en_US"
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section__title">Welcome Message Template</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Optional utility template sent after verification. Body variables are fixed in code
                (not editable here): <code>{'{{1}}'}</code> = full name, <code>{'{{2}}'}</code> = workspace sign-in URL.
                Use a Meta template with exactly those two body placeholders (e.g. <code>account_creation</code>).
              </p>
              <div className="form-group">
                <label htmlFor="welcomeEnabled" className="form-checkbox">
                  <input
                    type="checkbox"
                    id="welcomeEnabled"
                    name="welcomeEnabled"
                    checked={form.welcomeEnabled}
                    onChange={handleFormChange}
                  />
                  <span>Send welcome message after signup verification</span>
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="welcomeTemplateName" className="form-label">Welcome Template Name</label>
                <input
                  type="text"
                  id="welcomeTemplateName"
                  name="welcomeTemplateName"
                  className="form-input"
                  value={form.welcomeTemplateName}
                  onChange={handleFormChange}
                  required={form.welcomeEnabled}
                  placeholder="e.g. signup_welcome"
                  disabled={!form.welcomeEnabled}
                />
              </div>
              <div className="form-group">
                <label htmlFor="welcomeTemplateLanguage" className="form-label">Welcome Template Language</label>
                <input
                  type="text"
                  id="welcomeTemplateLanguage"
                  name="welcomeTemplateLanguage"
                  className="form-input"
                  value={form.welcomeTemplateLanguage}
                  onChange={handleFormChange}
                  placeholder="en_US"
                  disabled={!form.welcomeEnabled}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving...' : editingConfig ? 'Save Changes' : 'Create Configuration'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showTestModal && testingConfig && (
        <Modal
          title="Send Test OTP"
          subtitle={testingConfig.name}
          onClose={() => setShowTestModal(false)}
        >
          <form onSubmit={handleTestSend}>
            <div className="form-group">
              <label htmlFor="testPhone" className="form-label">
                Phone number (E.164) <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="testPhone"
                className="form-input"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                required
                placeholder="+919876543210"
              />
              <span className="form-hint">Sends OTP template with code 123456 for testing.</span>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setShowTestModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={testingId === testingConfig._id}>
                {testingId === testingConfig._id ? 'Sending...' : 'Send Test OTP'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default WhatsAppConfig;
